import config from '../config/environments'
import { stackService } from './stackService'
import { tokenRefreshService } from './tokenRefreshService'
import { errorService } from './errorService'

/**
 * Standard API response format from backend
 */
export interface ApiResponse<T> {
  success: boolean
  data?: T
  message?: string
  errors?: Array<{
    code: string
    message: string
    details?: any
  }>
  meta?: {
    total?: number
    page?: number
    pageSize?: number
    [key: string]: any
  }
}

/**
 * HTTP error with response details
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public errors?: any[]
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * Base API Service
 * Handles authenticated HTTP requests to backend with JWT tokens
 *
 * Multi-Stack Support:
 * - Uses stackService to determine which API Gateway stack to hit
 * - Base URL includes stack-specific base path (e.g., /sbass, /dev, /jbailey)
 * - Listens for stack changes and updates base URL automatically
 */
export class ApiService {
  private baseUrl: string

  constructor() {
    // Get base URL with stack-specific base path
    this.baseUrl = stackService.getApiBaseUrl(config.apiBaseUrl)

    // Listen for stack changes and update base URL
    window.addEventListener('stack-changed', () => {
      this.updateBaseUrl()
    })
  }

  /**
   * Update base URL when stack selection changes
   */
  private updateBaseUrl(): void {
    this.baseUrl = stackService.getApiBaseUrl(config.apiBaseUrl)
    console.log('🔄 API base URL updated:', this.baseUrl)
  }

  /**
   * Build headers for authenticated requests
   * Phase 4.4: No Authorization header needed - httpOnly cookies handle auth
   */
  private getHeaders(): HeadersInit {
    return {
      'Content-Type': 'application/json',
    }
  }

  /**
   * Check if this endpoint should skip token refresh check
   * (to avoid infinite loop when calling /auth/refresh itself)
   */
  private shouldSkipTokenCheck(endpoint: string): boolean {
    return endpoint.includes('/auth/refresh') ||
           endpoint.includes('/auth/callback') ||
           endpoint.includes('/auth/logout')
  }

  /**
   * Ensure token is valid before making API call
   * Returns false if token refresh failed and request should be aborted
   */
  private async ensureValidToken(endpoint: string): Promise<boolean> {
    if (this.shouldSkipTokenCheck(endpoint)) {
      return true // Skip check for auth endpoints
    }

    return await tokenRefreshService.ensureValidToken()
  }

  /**
   * Generic GET request
   * @param endpoint - API endpoint path
   * @param options - Optional settings
   * @param options.suppressErrors - If true, don't show error modals (useful for probing/fallback logic)
   */
  async get<T>(endpoint: string, options?: { suppressErrors?: boolean }): Promise<ApiResponse<T>> {
    try {
      // Ensure token is valid before making request
      const tokenValid = await this.ensureValidToken(endpoint)
      if (!tokenValid) {
        return {
          success: false,
          message: 'Token refresh failed',
          errors: [{ code: 'TOKEN_REFRESH_FAILED', message: 'Failed to refresh authentication token' }]
        }
      }

      const url = `${this.baseUrl}${endpoint}`
      console.log('🌐 GET:', url)

      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include', // Send httpOnly cookies with request
      })

      return await this.handleResponse<T>(response, options?.suppressErrors)
    } catch (error) {
      return this.handleError<T>(error, options?.suppressErrors)
    }
  }

  /**
   * Generic POST request
   */
  async post<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    try {
      // Ensure token is valid before making request
      const tokenValid = await this.ensureValidToken(endpoint)
      if (!tokenValid) {
        return {
          success: false,
          message: 'Token refresh failed',
          errors: [{ code: 'TOKEN_REFRESH_FAILED', message: 'Failed to refresh authentication token' }]
        }
      }

      const url = `${this.baseUrl}${endpoint}`
      console.log('🌐 POST:', url)
      console.log('🔍 POST - Headers:', this.getHeaders())
      console.log('🔍 POST - Body:', data ? JSON.stringify(data, null, 2) : 'undefined')

      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(),
        credentials: 'include', // Send httpOnly cookies with request
        body: data ? JSON.stringify(data) : undefined,
      })

      console.log('🔍 POST - Response status:', response.status)
      console.log('🔍 POST - Response ok:', response.ok)

      return await this.handleResponse<T>(response)
    } catch (error) {
      console.error('🔍 POST - Caught error:', error)
      return this.handleError<T>(error)
    }
  }

  /**
   * Generic PUT request with optimistic locking via body
   * @param endpoint - API endpoint path
   * @param data - Request body data (include 'version' field for optimistic locking)
   *
   * Note: For optimistic locking, include the version field in the data payload.
   * The backend extracts 'version' from the body automatically.
   */
  async put<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    try {
      // Ensure token is valid before making request
      const tokenValid = await this.ensureValidToken(endpoint)
      if (!tokenValid) {
        return {
          success: false,
          message: 'Token refresh failed',
          errors: [{ code: 'TOKEN_REFRESH_FAILED', message: 'Failed to refresh authentication token' }]
        }
      }

      const url = `${this.baseUrl}${endpoint}`
      console.log('🌐 PUT:', url)

      const response = await fetch(url, {
        method: 'PUT',
        headers: this.getHeaders(),
        credentials: 'include', // Send httpOnly cookies with request
        body: JSON.stringify(data),
      })

      return await this.handleResponse<T>(response)
    } catch (error) {
      return this.handleError<T>(error)
    }
  }

  /**
   * Generic DELETE request
   */
  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    try {
      // Ensure token is valid before making request
      const tokenValid = await this.ensureValidToken(endpoint)
      if (!tokenValid) {
        return {
          success: false,
          message: 'Token refresh failed',
          errors: [{ code: 'TOKEN_REFRESH_FAILED', message: 'Failed to refresh authentication token' }]
        }
      }

      const url = `${this.baseUrl}${endpoint}`
      console.log('🌐 DELETE:', url)

      const response = await fetch(url, {
        method: 'DELETE',
        headers: this.getHeaders(),
        credentials: 'include', // Send httpOnly cookies with request
      })

      return await this.handleResponse<T>(response)
    } catch (error) {
      return this.handleError<T>(error)
    }
  }

  /**
   * Generic bulk operation request
   * @param endpoint - Base endpoint (e.g., '/field_configurations', '/offerings')
   * @param operation - Bulk operation type
   * @param items - Array of items to process
   * @returns API response with bulk operation results
   */
  async bulkOperation<T>(
    endpoint: string,
    operation: 'insert' | 'update' | 'upsert' | 'delete' | 'undelete' | 'purge',
    items: any[]
  ): Promise<ApiResponse<T>> {
    const bulkEndpoint = `${endpoint}/bulk/${operation}`
    const payload = { items }

    console.log(`🔍 bulk${operation} - Endpoint: ${bulkEndpoint}`)
    console.log(`🔍 bulk${operation} - Items count:`, items.length)

    const response = await this.post<T>(bulkEndpoint, payload)

    if (!response.success) {
      console.error(`Failed to bulk ${operation}:`, response.errors)
    }

    return response
  }

  /**
   * Handle HTTP response and parse JSON
   * @param response - Fetch Response object
   * @param suppressErrors - If true, don't show error modals
   */
  private async handleResponse<T>(response: Response, suppressErrors?: boolean): Promise<ApiResponse<T>> {
    const contentType = response.headers.get('content-type')
    const hasJson = contentType && contentType.includes('application/json')

    // Parse response body
    let body: any
    try {
      body = hasJson ? await response.json() : await response.text()
    } catch (error) {
      console.error('Failed to parse response:', error)
      body = null
    }

    // Handle error responses
    if (!response.ok) {
      console.error(`❌ API Error ${response.status}:`, body)

      const errorMessage = body?.message || body?.errors?.[0]?.message || `HTTP ${response.status}: ${response.statusText || 'Error'}`
      const errorDetails = body?.errors || [
        {
          code: `HTTP_${response.status}`,
          message: response.statusText,
        },
      ]

      // Display error via ErrorService (unless suppressed)
      if (!suppressErrors) {
        errorService.showError(
          'API Request Failed',
          errorMessage,
          errorDetails
        )
      }

      return {
        success: false,
        message: errorMessage,
        errors: errorDetails,
      }
    }

    // Success response
    console.log('✅ API Success:', body)

    // Backend returns data in standard format
    return {
      success: true,
      data: body?.data || body,
      message: body?.message,
      meta: body?.meta,
    }
  }

  /**
   * Handle network and other errors
   * @param error - The caught error
   * @param suppressErrors - If true, don't show error modals
   */
  private handleError<T>(error: any, suppressErrors?: boolean): ApiResponse<T> {
    console.error('❌ API Request Error:', error)

    const errorMessage = error.message || 'An unknown error occurred'
    const errorDetails = [
      {
        code: 'NETWORK_ERROR',
        message: error.message || 'Failed to connect to API',
      },
    ]

    // Display error via ErrorService (unless suppressed)
    if (!suppressErrors) {
      errorService.showError(
        'Network Error',
        errorMessage,
        errorDetails
      )
    }

    return {
      success: false,
      message: errorMessage,
      errors: errorDetails,
    }
  }
}

// Export singleton instance
export const apiService = new ApiService()
export default apiService
